import { FormattedMessage, useIntl } from 'react-intl';
import { CRow, CCol, CCard, CCardHeader, CCardBody, CButton } from '@coreui/react';
import DataTable from 'components/common/DataTable';
import * as yup from 'yup';
import { emailInvalid, nullTypeError } from 'utils/yup';
import { Formik, Form, Field, type FormikHelpers } from 'formik';
import TextInput from 'components/inputs/TextInput';
import { Link } from 'react-router-dom';
import routePaths from 'router/route-paths';
import Button from 'components/common/Button';
import type { IAdminRow, ITableField } from 'components/admins/AdminsTable';
import s from './LoanConsultantsTable.module.scss';

export interface ILoanConsultantFormValues {
  email: string;
  firstName: string;
  lastName: string;
}

interface ILoanConsultantsTableProps {
  onAddLoanConsultant: (
    values: ILoanConsultantFormValues,
    formikActions: FormikHelpers<ILoanConsultantFormValues>,
  ) => Promise<void>;
  onDeleteLoanConsultant: (loanConsultant: IAdminRow) => Promise<void>;
  fields: ITableField[];
  loanConsultants: IAdminRow[];
}

const initialValue: ILoanConsultantFormValues = {
  email: '',
  firstName: '',
  lastName: '',
};

const schema = yup.object().shape({
  email: yup
    .string()
    .email(emailInvalid)
    .required(() => <FormattedMessage id="errors.fill_field" />)
    .nonNullable(nullTypeError('string')),
  firstName: yup
    .string()
    .required(() => <FormattedMessage id="errors.fill_field" />)
    .nonNullable(nullTypeError('string')),
  lastName: yup
    .string()
    .required(() => <FormattedMessage id="errors.fill_field" />)
    .nonNullable(nullTypeError('string')),
});

const LoanConsultantsTable = ({
  onAddLoanConsultant,
  onDeleteLoanConsultant,
  fields,
  loanConsultants,
}: ILoanConsultantsTableProps) => {
  const { formatMessage: t } = useIntl();
  return (
    <CRow>
      <CCol xl={8} className="mx-auto">
        <CCard>
          <CCardHeader className={s.header}>
            <div>{t({ id: 'loan_consultants.title' })}</div>
            <Link to={routePaths.reports}>
              <Button color="primary">{t({ id: 'reports.generate_report' })}</Button>
            </Link>
          </CCardHeader>

          <Formik
            initialValues={initialValue}
            validationSchema={schema}
            onSubmit={onAddLoanConsultant}
          >
            {({ isSubmitting }) => (
              <Form>
                <div className={s.card}>
                  <Field
                    type="email"
                    name="email"
                    component={TextInput}
                    required
                    label={t({ id: 'auth.email' })}
                  />
                  <Field
                    type="text"
                    name="firstName"
                    component={TextInput}
                    required
                    label={t({ id: 'user.first_name' })}
                  />
                  <Field
                    type="text"
                    name="lastName"
                    component={TextInput}
                    required
                    label={t({ id: 'user.last_name' })}
                  />
                  <div style={{ textAlign: 'right' }}>
                    <CButton disabled={isSubmitting} color="primary" type="submit">
                      {t({ id: 'loan_consultants.add_loan_consultant_btn' })}
                    </CButton>
                  </div>
                </div>
              </Form>
            )}
          </Formik>

          <CCardBody>
            <DataTable
              items={loanConsultants}
              fields={fields}
              addTableClasses={s.loanConsultantsTable}
              scopedSlots={{
                delete: ({ name, email }: IAdminRow) => (
                  <td>
                    <CButton
                      className="d-block w-100"
                      color="danger"
                      variant="outline"
                      onClick={() => onDeleteLoanConsultant({ email, name })}
                    >
                      {t({ id: 'loan_consultants.delete' })}
                    </CButton>
                  </td>
                ),
              }}
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default LoanConsultantsTable;
